// Access tokens for AI apps on /devices (TOM-841, ADR 0413): a personal access
// token an owner makes so an AI app or their own script can read their boats
// through the MCP server at /mcp. A token is scoped, expires, can never call
// /v1, and is shown once. Making one needs a sign-in from the last ten minutes,
// as a pairing key does (PairingKeys.tsx). The section hides itself when the
// server answers 404 (NEOMARIS_CLOUD_MCP_ENABLED off). TOM-845 grows this into
// the full Connected apps page.
//
// McpTokensView draws every state from props (tested under Node, as the web
// package has no DOM test environment); McpTokens owns the requests.

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api';
import { useAuth } from '../auth';

export type McpScope = 'boat:read' | 'neo:ask';

/** One live grant, as `GET /v1/mcp/grants` returns it (contract §9.4). */
export interface McpGrant {
  id: string;
  kind: 'pat' | 'oauth';
  label: string;
  clientName: string | null;
  scopes: string[];
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
}

export interface MadeMcpToken {
  grant: McpGrant;
  token: string;
}

interface ApiError {
  status?: number;
  body?: { error?: string; message?: string };
  message?: string;
}

/** The scopes an owner can choose, in the order the form shows them. */
export const MCP_SCOPES: readonly { scope: McpScope; title: string; help: string }[] = [
  {
    scope: 'boat:read',
    title: 'Read boat data',
    help: 'Positions, readings, alarms, forecasts and logs for all your boats.',
  },
  {
    scope: 'neo:ask',
    title: 'Ask Neo questions',
    help: 'Ask Neo about your boats and get its answers.',
  },
];

export const MCP_EXPIRY_DAYS = [30, 90, 365] as const;
export const DEFAULT_MCP_EXPIRY_DAYS = 90;

function formatWhen(iso: string | null): string {
  if (iso === null) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** A grant's scopes in the form's own words; an unknown scope shows as itself. */
export function scopeTitles(scopes: string[]): string {
  return scopes.map((s) => MCP_SCOPES.find((m) => m.scope === s)?.title ?? s).join(', ');
}

/**
 * What kind of grant a row is: a token, or an app connected through OAuth
 * (TOM-843), whose row is already named after the app.
 */
export function grantKindLabel(grant: Pick<McpGrant, 'kind'>): string {
  return grant.kind === 'pat' ? 'Access token' : 'Connected app';
}

/** A grant's name: an app's own name, or the label the owner gave a token. */
export function grantName(grant: Pick<McpGrant, 'kind' | 'label' | 'clientName'>): string {
  return grant.kind === 'oauth' ? (grant.clientName ?? grant.label) : grant.label;
}

/** The MCP server's URL on this origin. */
export function mcpServerUrl(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/mcp`;
}

/** What the browser asks before a grant is revoked. */
export function revokeGrantConfirmation(
  grant: Pick<McpGrant, 'kind' | 'label' | 'clientName'>
): string {
  if (grant.kind === 'oauth') {
    return `Disconnect “${grantName(grant)}”? It loses access to your boats at once.`;
  }
  return `Revoke the access token “${grant.label}”? Anything using it loses access to your boats at once.`;
}

/**
 * How a failed grants load is shown: `hidden` when MCP is turned off on this
 * server (404), so the section never appears; otherwise the message to show.
 */
export function grantsLoadFailure(err: ApiError): { hidden: true } | { message: string } {
  if (err.status === 404) return { hidden: true };
  return { message: err.body?.message ?? err.message ?? 'Couldn’t load your access tokens.' };
}

/** How a failed make is shown: a fresh sign-in, or a message. */
export function makeTokenFailure(err: ApiError): { needsSignIn: true } | { message: string } {
  const code = err.body?.error;
  if (code === 'reauth_required') return { needsSignIn: true };
  if (code === 'browser_session_required') {
    return { message: 'Make an access token in a web browser, signed in to your account.' };
  }
  if (code === 'too_many_tokens') {
    return { message: 'You have 10 access tokens already. Revoke one to make another.' };
  }
  return { message: err.body?.message ?? err.message ?? 'Couldn’t make an access token.' };
}

export interface McpTokensViewProps {
  grants: McpGrant[] | null;
  /** The token just made: shown once, until the owner dismisses it. */
  made: MadeMcpToken | null;
  serverUrl: string;
  label: string;
  scopes: McpScope[];
  expiresInDays: number;
  making: boolean;
  /** Making a token needs a fresh sign-in (403 reauth_required). */
  needsSignIn: boolean;
  error: string | null;
  copied: boolean;
  revokingId: string | null;
  onLabel: (label: string) => void;
  onScope: (scope: McpScope, on: boolean) => void;
  onExpiry: (days: number) => void;
  onMake: (e: FormEvent) => void;
  onCopy: () => void;
  onDismiss: () => void;
  onSignInAgain: () => void;
  onRevoke: (grant: McpGrant) => void;
}

export function McpTokensView(props: McpTokensViewProps) {
  const { grants, made } = props;
  const canMake = !props.making && props.label.trim().length > 0 && props.scopes.length > 0;
  return (
    <section className="profile-section devices-keys" data-testid="mcp-tokens">
      <h3 className="profile-section-heading">AI apps (MCP)</h3>
      <p className="profile-section-help">
        An access token lets an AI app or your own script read your boats through Neomaris’s MCP
        server. It can’t change anything on a boat or in your account, and it expires. Revoke it
        here at any time. Apps you connect from the app itself, such as Claude or ChatGPT, are
        listed here too, as connected apps.
      </p>
      <p className="profile-section-help mcp-server-url" data-testid="mcp-server-url">
        Server URL: <code>{props.serverUrl}</code>. Add it to your MCP client and send the token as{' '}
        <code>Authorization: Bearer</code>.
      </p>

      {props.error !== null && (
        <ul className="form-errors" data-testid="mcp-error">
          <li>{props.error}</li>
        </ul>
      )}

      {props.needsSignIn && (
        <div className="devices-key-signin" role="alert" data-testid="mcp-sign-in">
          <p>
            To make an access token, sign in again, then come back to Linked devices and make it
            within ten minutes.
          </p>
          <button type="button" className="btn btn-primary" onClick={props.onSignInAgain}>
            Sign in again
          </button>
        </div>
      )}

      {made !== null ? (
        <div className="tb-card devices-key-made" data-testid="mcp-made">
          <p className="devices-key-made__title">
            <strong>{made.grant.label}</strong>: copy the token now. It won’t be shown again.
          </p>
          <code className="devices-key-made__key" data-testid="mcp-token-value">
            {made.token}
          </code>
          <p className="devices-list__meta">
            Store it like a password. Anyone who holds it can read your boats until it expires on{' '}
            {formatWhen(made.grant.expiresAt)} or you revoke it.
          </p>
          <div className="devices-key-made__actions">
            <button type="button" className="btn btn-primary" onClick={props.onCopy}>
              {props.copied ? 'Copied' : 'Copy token'}
            </button>
            <button type="button" className="btn" onClick={props.onDismiss}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <form className="devices-key-form mcp-token-form" onSubmit={props.onMake}>
          <div className="form-group">
            <label htmlFor="mcp-token-label">Name</label>
            <input
              id="mcp-token-label"
              className="form-input"
              type="text"
              maxLength={80}
              placeholder="Claude on my laptop"
              value={props.label}
              onChange={(e) => props.onLabel(e.target.value)}
            />
          </div>
          <fieldset className="mcp-token-scopes">
            <legend>It can</legend>
            {MCP_SCOPES.map((s) => (
              <label key={s.scope} className="mcp-token-scope">
                <input
                  type="checkbox"
                  checked={props.scopes.includes(s.scope)}
                  onChange={(e) => props.onScope(s.scope, e.target.checked)}
                />
                <span>
                  <strong>{s.title}</strong>
                  <span className="devices-list__meta">{s.help}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="form-group mcp-token-expiry">
            <label htmlFor="mcp-token-expiry">Expires after</label>
            <select
              id="mcp-token-expiry"
              className="tb-select"
              value={props.expiresInDays}
              onChange={(e) => props.onExpiry(Number(e.target.value))}
            >
              {MCP_EXPIRY_DAYS.map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={!canMake}>
            {props.making ? 'Making…' : 'Make a token'}
          </button>
        </form>
      )}

      {grants !== null && grants.length > 0 && (
        <ul className="tb-list devices-list" data-testid="mcp-grant-list">
          {grants.map((g) => (
            <li key={g.id} className="tb-list__row" data-testid="mcp-grant">
              <span className="tb-list__body">
                <span className="devices-list__text">
                  <strong className="devices-list__name">{grantName(g)}</strong>
                  <span className="devices-list__meta">
                    {grantKindLabel(g)} · {scopeTitles(g.scopes)}
                  </span>
                  <span className="devices-list__meta">
                    made {formatWhen(g.createdAt)} · expires{' '}
                    {g.expiresAt === null ? 'never' : formatWhen(g.expiresAt)} · last used{' '}
                    {formatWhen(g.lastUsedAt)}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn devices-revoke"
                  onClick={() => props.onRevoke(g)}
                  disabled={props.revokingId === g.id}
                >
                  {props.revokingId === g.id ? 'Revoking…' : 'Revoke'}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The section on /devices; draws nothing when MCP is turned off on this server. */
export function McpTokens() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);
  const [grants, setGrants] = useState<McpGrant[] | null>(null);
  const [made, setMade] = useState<MadeMcpToken | null>(null);
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<McpScope[]>(['boat:read']);
  const [expiresInDays, setExpiresInDays] = useState<number>(DEFAULT_MCP_EXPIRY_DAYS);
  const [making, setMaking] = useState(false);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<{ grants: McpGrant[] }>('/v1/mcp/grants');
      setGrants(res.grants);
    } catch (err) {
      const failure = grantsLoadFailure(err as ApiError);
      if ('hidden' in failure) setHidden(true);
      else {
        setError(failure.message);
        setGrants([]);
      }
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [load]);

  function toggleScope(scope: McpScope, on: boolean) {
    setScopes((prev) => {
      const rest = prev.filter((s) => s !== scope);
      return on ? [...rest, scope] : rest;
    });
  }

  async function make(e: FormEvent) {
    e.preventDefault();
    setMaking(true);
    setError(null);
    setNeedsSignIn(false);
    try {
      const res = await api<MadeMcpToken>('/v1/mcp/tokens', {
        method: 'POST',
        body: {
          label: label.trim(),
          scopes: MCP_SCOPES.map((s) => s.scope).filter((s) => scopes.includes(s)),
          expiresInDays,
        },
        idempotencyKey: crypto.randomUUID(),
      });
      setMade(res);
      setLabel('');
      await load();
    } catch (err) {
      const failure = makeTokenFailure(err as ApiError);
      if ('needsSignIn' in failure) setNeedsSignIn(true);
      else setError(failure.message);
    } finally {
      setMaking(false);
    }
  }

  function copy() {
    if (made === null || typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard
      .writeText(made.token)
      .then(() => {
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard denied: the token is still on screen to select by hand.
      });
  }

  function dismiss() {
    setMade(null);
    setCopied(false);
  }

  async function signInAgain() {
    await logout();
    navigate('/login', { state: { from: '/devices' } });
  }

  async function revoke(grant: McpGrant) {
    if (typeof window !== 'undefined' && !window.confirm(revokeGrantConfirmation(grant))) return;
    setRevokingId(grant.id);
    setError(null);
    try {
      await api(`/v1/mcp/grants/${grant.id}`, {
        method: 'DELETE',
        idempotencyKey: crypto.randomUUID(),
      });
      if (made?.grant.id === grant.id) dismiss();
      await load();
    } catch (err) {
      const e = err as ApiError;
      setError(e.body?.message ?? e.message ?? 'Couldn’t revoke that token.');
    } finally {
      setRevokingId(null);
    }
  }

  if (hidden) return null;

  return (
    <McpTokensView
      grants={grants}
      made={made}
      serverUrl={mcpServerUrl(typeof window === 'undefined' ? '' : window.location.origin)}
      label={label}
      scopes={scopes}
      expiresInDays={expiresInDays}
      making={making}
      needsSignIn={needsSignIn}
      error={error}
      copied={copied}
      revokingId={revokingId}
      onLabel={setLabel}
      onScope={toggleScope}
      onExpiry={setExpiresInDays}
      onMake={(e) => void make(e)}
      onCopy={copy}
      onDismiss={dismiss}
      onSignInAgain={() => void signInAgain()}
      onRevoke={(g) => void revoke(g)}
    />
  );
}
